<script setup lang="ts">
import { computed } from "vue";

import ChatHistoryDropdown from "~/components/chat/ChatHistoryDropdown.vue";
import Button from "~/components/dumb/Button.vue";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { copyAndNotify } from "~/components/notifications";
import { ChevronLeftIcon, CopyIcon, DotsHorizontalIcon, HistoryFieldIcon, PlusIcon, TrashIcon, XIcon } from "~/icons";
import { ButtonSize, ButtonStyle, DropdownMenuItemKind, IconSize, Placement } from "~/shared/enums";
import type { Chat } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore } from "~/stores";

const props = defineProps<{
  chat: Chat;
}>();

const emit = defineEmits<{
  create: [];
  close: [];
  select: [duid: string];
  delete: [duid: string];
}>();

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();

const closeChat = () => {
  emit("close");
};

const openDeleteConfirmationModal = (duid: string) => {
  const chat = dataStore.getChatByDuid(duid);
  if (!chat) {
    return;
  }

  appStore.setDeleteConfirmationModalOpen({
    title: `Delete ${chat.title}`,
    description: "Permanently deleting a chat can't be undone. Are you sure you want to proceed?",
    onDelete: () => emit("delete", duid),
  });
};

const dropdownSections = computed(() => [
  {
    title: "Chat",
    items: [
      {
        title: "Copy ID",
        kind: DropdownMenuItemKind.BUTTON,
        icon: CopyIcon,
        onClick: () => copyAndNotify("Chat ID", props.chat.duid),
      },
      {
        title: "Delete chat",
        kind: DropdownMenuItemKind.BUTTON,
        icon: TrashIcon,
        onClick: () => openDeleteConfirmationModal(props.chat.duid),
      },
    ],
  },
]);
</script>

<template>
  <div class="flex w-full items-center justify-between py-2 pl-[15px] pr-2">
    <div class="flex items-center gap-2">
      <button
        v-if="pageStore.isMobile"
        type="button"
        class="flex items-center rounded p-1 text-lt focus-ring-std hover:bg-md"
        @click="closeChat"
        @keydown.enter="closeChat">
        <span class="sr-only">Close chat</span>
        <ChevronLeftIcon class="icon-sm" />
      </button>

      <Tooltip text="Create a new chat">
        <Button
          text="New chat"
          text-style="text-lt"
          :icon="PlusIcon"
          :icon-size="IconSize.XS"
          :btn-style="ButtonStyle.SECONDARY"
          :size="ButtonSize.CHIP"
          class="!py-0.5 !pl-1 leading-[18px]"
          @click="() => emit('create')" />
      </Tooltip>
    </div>

    <div class="flex items-center gap-0.5">
      <ChatHistoryDropdown
        :chat-duid="chat.duid"
        @select="(duid) => emit('select', duid)"
        @delete="(duid) => openDeleteConfirmationModal(duid)">
        <Tooltip text="Chat history">
          <button type="button" class="flex items-center rounded p-1 text-lt focus-ring-std hover:bg-md">
            <span class="sr-only">Select chat</span>
            <HistoryFieldIcon class="icon-sm" />
          </button>
        </Tooltip>
      </ChatHistoryDropdown>

      <DropdownMenu :sections="dropdownSections" :placement="Placement.BOTTOM_RIGHT" :distance="2">
        <Tooltip text="Options">
          <button type="button" class="flex items-center rounded p-1 text-lt focus-ring-std hover:bg-md">
            <span class="sr-only">Chat options</span>
            <DotsHorizontalIcon class="icon-sm" />
          </button>
        </Tooltip>
      </DropdownMenu>

      <Tooltip v-if="!pageStore.isMobile" text="Close chat">
        <button
          type="button"
          class="flex items-center rounded p-1 text-lt focus-ring-std hover:bg-md"
          @click="closeChat">
          <span class="sr-only">Close chat</span>
          <XIcon class="icon-sm" />
        </button>
      </Tooltip>
    </div>
  </div>
</template>
